import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function AccountantSignup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", type: "" });

  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const userCred = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      await setDoc(doc(db, "users", userCred.user.uid), {
        role: "accountant",
        createdAt: serverTimestamp(),
        email,
      });

      await api.post("/auth/signup", {
        email,
        role: "accountant",
      });

      setSnack({
        open: true,
        message: "Signup successful! Redirecting...",
        type: "success",
      });

      setTimeout(() => {
        navigate("/accountant/pricing");
      }, 1000);
    } catch (err) {
      console.error("Signup error:", err);
      setSnack({
        open: true,
        message: err.message || "Signup failed. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f9fafc",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{ p: 4, borderRadius: 3, textAlign: "center" }}
        >
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Accountant Portal Signup
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Create an account to manage your clients, connect your accounting
            software, and access advanced reporting.
          </Typography>

          <TextField
            fullWidth
            label="Email"
            type="email"
            variant="outlined"
            sx={{ mb: 2 }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            variant="outlined"
            sx={{ mb: 3 }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: "#22c55e",
              fontWeight: 600,
              py: 1.2,
              "&:hover": { backgroundColor: "#16a34a" },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Create Account"
            )}
          </Button>

          <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
            <Typography variant="caption" color="text.secondary">
              ✅ Trusted by 100+ accounting firms
            </Typography>
            <Typography variant="caption" color="text.secondary">
              🔒 Secure and compliant
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ⭐ 5-star rating by early adopters
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            mt={2}
            display="block"
          >
            No credit card required. Cancel anytime.
          </Typography>
        </Paper>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity={snack.type} variant="filled">
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
